/**
 * Generate, then edit.
 *
 * The draft is shown in full before anything is written — entity types with
 * their properties, the relationships, and the DQ, match and survivorship rules
 * — because accepting a model you have not read is how you end up with a graph
 * nobody trusts. Applying it lands ordinary editable rows.
 */

import { useState } from 'react'
import { Modal, Spinner } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { formatError } from '@/core/utils/error.formatter'
import { CategoryBadge, FlagChip, SeverityChip } from '../components'
import { useGenerate, useGeneratorPreview } from '../hooks'
import type { DraftResult } from '../types/brain.types'

const EXAMPLES = [
  'Track our customer accounts, the people we deal with there, our open deals, and any risks raised in email or documents.',
  'Model our suppliers, the parts they ship, purchase orders and quality incidents, so we can see which supplier a defect traces back to.',
  'Patients, their appointments, the clinicians they see and the documents each visit produces.',
]

export function GenerateDialog({
  graphId,
  onClose,
  onDone,
}: {
  graphId?: string
  onClose: () => void
  onDone: (graphId: string) => void
}) {
  const preview = useGeneratorPreview()
  const generate = useGenerate()
  const toast = useToast()

  const [requirement, setRequirement] = useState('')
  const [draft, setDraft] = useState<DraftResult | null>(null)
  const [name, setName] = useState('')

  const busy = preview.isPending || generate.isPending

  async function onPreview() {
    try {
      const result = await preview.mutateAsync({ requirement: requirement.trim(), graphId })
      setDraft(result)
      setName((current) => current || result.proposal.name || 'Brain')
    } catch (error) {
      toast.error(error)
    }
  }

  async function onApply() {
    if (!draft) return
    try {
      const result = await generate.mutateAsync({
        requirement: requirement.trim(),
        name: name.trim() || undefined,
        proposal: draft.proposal,
        graphId,
      })
      const applied = result.applied
      const skipped = applied.skipped.length
      toast.success(
        `Added ${applied.node_types} node types, ${applied.edge_types} relationships and ` +
          `${applied.dq_rules + applied.match_rules + applied.survivorship_rules} rules.` +
          (skipped ? ` ${skipped} item${skipped === 1 ? '' : 's'} skipped.` : ''),
      )
      onDone(result.graph_id)
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      title={graphId ? 'Generate into this graph' : 'Generate a graph'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {draft ? (
            <>
              <button className="btn" onClick={() => setDraft(null)} disabled={busy}>
                Back
              </button>
              <button className="btn btn-primary" onClick={onApply} disabled={busy}>
                {generate.isPending ? 'Applying…' : 'Apply this model'}
              </button>
            </>
          ) : (
            <button
              className="btn btn-primary"
              onClick={onPreview}
              disabled={busy || requirement.trim().length < 8}
            >
              {preview.isPending ? 'Drafting…' : 'Draft the model'}
            </button>
          )}
        </>
      }
    >
      {!draft && (
        <div className="stack">
          <div className="field">
            <label className="field-label" htmlFor="requirement">
              What do you need to keep track of?
            </label>
            <textarea
              id="requirement"
              className="textarea"
              rows={5}
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              placeholder="Describe the things you care about and how they relate. Plain language is fine."
            />
            <p className="field-help">
              Say what the things are and how they connect. The draft is proposed, not saved — you
              see it before it exists.
            </p>
          </div>

          <div className="stack" style={{ gap: 6 }}>
            <span className="small faint">Examples</span>
            {EXAMPLES.map((example) => (
              <button
                key={example}
                className="btn btn-ghost btn-sm"
                style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto' }}
                onClick={() => setRequirement(example)}
              >
                {example}
              </button>
            ))}
          </div>

          {preview.isPending && (
            <div className="row">
              <Spinner /> <span className="muted small">Designing the model…</span>
            </div>
          )}
          {preview.error && (
            <div className="callout callout-danger">
              <p>{formatError(preview.error).message}</p>
              <p className="small">
                Generation needs a language model on the API service. Without one you can still
                build the model by hand on the Nodes tab — every part of it is editable, and the
                pipeline runs either way for structured sources.
              </p>
              <p className="small">
                Set LLM_MODEL_CHAIN on the API service to a comma-separated list of models,
                best first — the chain falls past any one that is busy or retired.
              </p>
            </div>
          )}
        </div>
      )}

      {draft && <DraftReview draft={draft} name={name} onName={setName} showName={!graphId} />}
    </Modal>
  )
}

function DraftReview({
  draft,
  name,
  onName,
  showName,
}: {
  draft: DraftResult
  name: string
  onName: (next: string) => void
  showName: boolean
}) {
  const p = draft.proposal
  return (
    <div className="stack">
      {p.summary && (
        <div className="callout callout-accent">
          <p>{p.summary}</p>
        </div>
      )}

      {showName && (
        <div className="field">
          <label className="field-label" htmlFor="graph-name">
            Graph name
          </label>
          <input
            id="graph-name"
            className="input"
            value={name}
            onChange={(e) => onName(e.target.value)}
          />
        </div>
      )}

      <div className="row" style={{ gap: 14 }}>
        <span className="small muted">{p.node_types.length} node types</span>
        <span className="small muted">{p.edge_types.length} relationships</span>
        <span className="small muted">{p.dq_rules.length} quality rules</span>
        <span className="small muted">{p.match_rules.length} match rules</span>
        <span className="small muted">{p.survivorship_rules.length} survivorship rules</span>
      </div>

      <div className="stack" style={{ gap: 10 }}>
        {p.node_types.map((node) => (
          <div key={node.key} className="rulecard">
            <div className="rulehead">
              <span className="rulename">{node.key}</span>
              <CategoryBadge category={node.category} />
              <span className="spacer" />
              <span className="small faint">{node.properties.length} properties</span>
            </div>
            {node.description && <p className="small muted" style={{ margin: 0 }}>{node.description}</p>}
            <div className="stack" style={{ gap: 2 }}>
              {node.properties.map((prop) => (
                <div key={prop.key} className="proprow">
                  <span>{prop.key}</span>
                  <span className="row-tight">
                    <span className="faint">{prop.data_type}</span>
                    {prop.is_primary_key && <FlagChip flag="PK" />}
                    {prop.is_required && <FlagChip flag="REQ" />}
                    {prop.is_indexed && <FlagChip flag="IDX" />}
                    {prop.is_pii && <FlagChip flag="PII" />}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {p.edge_types.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Relationship</th>
                <th>From → to</th>
                <th>Cardinality</th>
                <th>Attributes</th>
              </tr>
            </thead>
            <tbody>
              {p.edge_types.map((edge) => (
                <tr key={`${edge.key}-${edge.from_node_type}-${edge.to_node_type}`}>
                  <td className="mono">{edge.key}</td>
                  <td>
                    {edge.from_node_type} → {edge.to_node_type}
                  </td>
                  <td>
                    <span className="badge badge-neutral">{edge.cardinality}</span>
                  </td>
                  <td className="mono small">
                    {edge.attributes.map((a) => a.key).join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {p.dq_rules.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Quality rule</th>
                <th>On</th>
                <th>Severity</th>
                <th>Check</th>
              </tr>
            </thead>
            <tbody>
              {p.dq_rules.map((rule) => (
                <tr key={`${rule.node_type}.${rule.key}`}>
                  <td>{rule.name}</td>
                  <td className="mono small">
                    {rule.node_type}
                    {rule.property_key ? `.${rule.property_key}` : ''}
                  </td>
                  <td>
                    <SeverityChip severity={rule.severity} />
                  </td>
                  <td className="mono small">{JSON.stringify(rule.definition)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {p.match_rules.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Match rule</th>
                <th>On</th>
                <th>Blocks on</th>
                <th>Compares</th>
                <th>Merge / review</th>
              </tr>
            </thead>
            <tbody>
              {p.match_rules.map((rule) => (
                <tr key={`${rule.node_type}.${rule.key}`}>
                  <td>{rule.name}</td>
                  <td className="mono small">{rule.node_type}</td>
                  <td className="mono small">{rule.blocking_keys.join(', ') || '—'}</td>
                  <td className="mono small">
                    {rule.comparators.map((c) => `${c.property_key}:${c.method}`).join(', ')}
                  </td>
                  <td className="mono small">
                    merge {rule.auto_merge_threshold.toFixed(2)}–1.00, review{' '}
                    {rule.review_threshold.toFixed(2)}–{rule.auto_merge_threshold.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {p.survivorship_rules.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Node type</th>
                <th>Property</th>
                <th>Winning value</th>
                <th>Source order</th>
              </tr>
            </thead>
            <tbody>
              {p.survivorship_rules.map((rule) => (
                <tr key={`${rule.node_type}.${rule.property_key}`}>
                  <td className="mono small">{rule.node_type}</td>
                  <td className="mono small">{rule.property_key}</td>
                  <td>{rule.strategy === 'source_system' ? 'Source system' : 'Recency'}</td>
                  <td className="mono small">
                    {rule.source_priority.length ? rule.source_priority.join(' → ') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="small faint" style={{ margin: 0 }}>
        Drafted by {draft.generated_by.provider}/{draft.generated_by.model} ·{' '}
        {draft.generated_by.prompt_version} · {draft.generated_by.attempts} attempt
        {draft.generated_by.attempts === 1 ? '' : 's'}
      </p>
    </div>
  )
}
